import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  TextField,
  Box,
  Alert,
  IconButton,
} from '@mui/material'
import {
  Close,
  Router,
  Public,
  Groups,
  SwapHoriz,
  Comment,
  Person,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { PlayerTransactionItem } from '@/state/player-transactions/models'
import { TransactionStatusEnum } from '@/enums'

interface TransactionDetailsModalProps {
  open: boolean
  onClose: () => void
  transaction: PlayerTransactionItem | null
}

export const TransactionDetailsModal: React.FC<
  TransactionDetailsModalProps
> = ({ open, onClose, transaction }) => {
  const { t } = useTranslation()

  if (!transaction) return null

  // Determine if there is an error to show
  const isError = transaction.transactionStatus === TransactionStatusEnum.ERROR

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" component="div">
          {
            t(
              'transaction.betDetails',
            ) /* Using betDetails or creating new Label? "Transactional Details" */
          }
          Transactional Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          {isError && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {/* Assuming comment contains the error message or a generic one */}
                <Typography variant="subtitle2">Error</Typography>
                {transaction.comment || 'Transaction Error'}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="IP Address"
              value={transaction.ipAddress || '-'}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <Router sx={{ mr: 1, color: 'action.active' }} />
                ),
              }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('labels.country')}
              value={transaction.country || '-'}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <Public sx={{ mr: 1, color: 'action.active' }} />
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('transaction.group')}
              value={transaction.group || '-'}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <Groups sx={{ mr: 1, color: 'action.active' }} />
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('transaction.transactionSubType')}
              value={transaction.transactionSubType || '-'}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <SwapHoriz sx={{ mr: 1, color: 'action.active' }} />
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('labels.comment')}
              value={transaction.comment || '-'}
              multiline
              rows={3}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <Comment sx={{ mr: 1, mt: 0.5, color: 'action.active' }} />
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('transaction.performedBy')}
              value={transaction.performedBy || '-'}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <Person sx={{ mr: 1, color: 'action.active' }} />
                ),
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          {t('labels.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
